import { useState, useEffect } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import {
  Notice,
  LoaderCircleIcon,
  CircleCheckBigIcon,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { FeedRow } from '~publish/pages/Community/components/FeedRow'

import { useBackfillCompletion } from '../../BackfillContext'
import { useBackfillLoadingBanner } from '../../hooks/backfill/useBackfillLoadingBanner'

import styles from './BackfillLoadingBanner.module.css'

export const BackfillLoadingBanner_Channel = graphql(`
  fragment BackfillLoadingBanner_Channel on Channel @_unmask {
    id
  }
`)

/**
 * `useBackfillLoadingBanner` still types its `channel` against the full codegen
 * `Channel`, so the channel flowing through this banner must satisfy both it
 * and the colocated fragment. The intersection collapses to the fragment type
 * once that hook migrates (CORE-6384).
 */
export type BackfillLoadingBannerChannel = FragmentOf<
  typeof BackfillLoadingBanner_Channel
> &
  Parameters<typeof useBackfillLoadingBanner>[0]['channel']

type BackfillLoadingBannerProps = {
  channel: BackfillLoadingBannerChannel
  /** Spans the feed grid width while keeping the banner's own max-width. */
  fullWidth?: boolean
  /**
   * Edge-to-edge with no max-width and no feed-grid alignment, for the by-post
   * view where the banner sits below the header across both columns.
   */
  fullBleed?: boolean
}

/**
 * Gets the appropriate loading message based on elapsed time
 * 0-7s: "Checking for new comments. It may take up to 2 minutes."
 * 7-15s: "Still working…"
 * 15s+: "Checking for new comments. Almost there…"
 */
function getLoadingMessage(elapsedSeconds?: number): string {
  if (elapsedSeconds === undefined || elapsedSeconds < 7) {
    return 'Checking for new comments. It may take up to 2 minutes.'
  }

  if (elapsedSeconds < 15) {
    return 'Still working…'
  }

  return 'Checking for new comments. Almost there…'
}

export const BackfillLoadingBanner = ({
  channel,
  fullWidth = false,
  fullBleed = false,
}: BackfillLoadingBannerProps): JSX.Element | null => {
  const channelId = channel?.id?.toString() ?? ''
  const { showBanner, elapsedSeconds } = useBackfillLoadingBanner({ channel })
  const { hasChannelCompleted, getChannelCompletionMessage } =
    useBackfillCompletion()

  // Force re-render every second to pick up completion state from refs
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!showBanner) return undefined
    const interval = setInterval(() => {
      setTick((t) => t + 1)
    }, 1000)
    return (): void => clearInterval(interval)
  }, [showBanner])

  if (!showBanner) {
    return null
  }

  const isCompleted = hasChannelCompleted(channelId)
  const completionMessage = getChannelCompletionMessage(channelId)

  // If completed with empty message (error case), hide banner immediately
  if (isCompleted && !completionMessage) {
    return null
  }

  const icon = isCompleted ? <CircleCheckBigIcon /> : <LoaderCircleIcon />
  const message =
    isCompleted && completionMessage
      ? completionMessage
      : getLoadingMessage(elapsedSeconds)

  if (fullBleed) {
    return (
      <Notice
        variant="neutral"
        icon={icon}
        className={clsx(styles.banner, styles.fullBleed)}
        data-backfill-banner
      >
        {message}
      </Notice>
    )
  }

  const notice = (
    <Notice
      variant="neutral"
      icon={icon}
      className={styles.banner}
      data-backfill-banner
    >
      {message}
    </Notice>
  )

  if (fullWidth) {
    return (
      <div className={styles.fullWidthWrapper}>
        <FeedRow>{notice}</FeedRow>
      </div>
    )
  }

  return <FeedRow>{notice}</FeedRow>
}
